import { useQuery } from "@tanstack/react-query";
import { api } from "../../../../singleton/axios";
import type { InsiderTradingData } from "./useInsiderTrading";
import type { InsiderPeriod } from "./insiderPeriods";

export const COMPANY_INSIDER_TRADING_QUERY_KEY = "companyInsiderTrading";

export interface CompanyInsiderTrade extends InsiderTradingData {
  accessionNumber: string;
  copiesDiffer: boolean;
  /**
   * Server-computed: whether shares × price is a cash equity amount. False for
   * an option, warrant or note, whose FMP `price` is a strike — those rows are
   * neither valued nor counted as buys/sells, exactly as in the alerts.
   */
  cashEquity: boolean;
}

/** GET /insider-trading/company */
export interface CompanyInsiderTrading {
  symbol: string;
  period: InsiderPeriod;
  /** `ticker` = display-only per-ticker fallback (no profile CIK, or no company-level data). */
  scope: "company" | "ticker";
  companyCik: string | null;
  companyName: string | null;
  /**
   * The viewed listing is an American Depositary share (null = unknown). An ADS
   * is a multiple of the share the filings count (TSMC's is five), so its
   * closing price cannot estimate a value for an unpriced award.
   */
  isAdr: boolean | null;
  /** Actively listed symbols of this company. More than one → every share class is shown together. */
  listedSymbols: string[];
  /** false → some filings couldn't be loaded; never present the result as a full period. */
  complete: boolean;
  incompleteFilings: number;
  /** Server-derived: whether alerts can exist for this company. NEVER inferred from `trades`. */
  canSubscribe: boolean;
  canSubscribeReason: "NOT_FOLLOWABLE" | "NO_INSIDER_DATA" | "UNAVAILABLE" | null;
  /** Already period-filtered and consolidated filing by filing. May be empty. */
  trades: CompanyInsiderTrade[];
}

/**
 * The COMPANY's insider activity for a period — every share class together,
 * resolved by CIK on the server, so GOOG and GOOGL show one history. Asks for
 * a period and gets whole filings back; it never walks FMP pages (that is what
 * split filings across requests in the legacy per-ticker route, which shipped
 * native binaries still call and this UI no longer does).
 */
export const useCompanyInsiderTrading = (symbol: string, period: InsiderPeriod) =>
  useQuery({
    queryKey: [COMPANY_INSIDER_TRADING_QUERY_KEY, symbol, period],
    queryFn: async (): Promise<CompanyInsiderTrading> => {
      const { data } = await api.get<CompanyInsiderTrading>("/insider-trading/company", {
        params: { symbol, period },
      });
      return data;
    },
    enabled: Boolean(symbol),
    // Switching PERIOD keeps the previous answer as a placeholder so the
    // company-level facts (title, CIK, canSubscribe) don't blink out. Never
    // across symbols — that would be another company's identity. Callers must
    // not show a placeholder's `trades`: they belong to another period.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === symbol ? previousData : undefined,
    staleTime: 2.5 * 60 * 1000,
    gcTime: 8 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
